// commonInfo.tsx
import React, { createContext, useContext, ReactNode, useReducer, Dispatch } from 'react';

// 定义公用信息类型
interface CommonInfo {
    projectId: number;
    subjectId: number;
    examSeasonId: number;
    examSyllabusId: number;
    sourceFromType: number;
    businessParam?: { [key: string]: number }; // 课程场景下可能会传
}

// 初始状态
const initialCommonInfo: CommonInfo = {
    projectId: 0,
    subjectId: 0,
    examSeasonId: 0,
    examSyllabusId: 0,
    sourceFromType: 0,
};

// 定义动作类型
type CommonInfoAction =
    | {
          type: 'UPDATE_COMMON_INFO';
          projectId: number;
          subjectId: number;
          examSeasonId: number;
          examSyllabusId: number;
          sourceFromType: number;
      }
    | { type: 'RESET_COMMON_INFO' };

// 定义 reducer 函数
function commonInfoReducer(state: CommonInfo, action: CommonInfoAction): CommonInfo {
    switch (action.type) {
        case 'UPDATE_COMMON_INFO':
            return {
                ...state,
                projectId: action.projectId,
                subjectId: action.subjectId,
                examSeasonId: action.examSeasonId,
                examSyllabusId: action.examSyllabusId,
                sourceFromType: action.sourceFromType,
            };
        case 'RESET_COMMON_INFO':
            return {
                ...initialCommonInfo,
            };
        default:
            throw new Error(`Unknown action type: ${(action as CommonInfoAction).type}`);
    }
}

// 定义上下文数据类型
interface CommonInfoContextData {
    commonInfo: CommonInfo;
    dispatch: Dispatch<CommonInfoAction>;
}

// 创建上下文
const CommonInfoContext = createContext<CommonInfoContextData | undefined>(undefined);

// 使用自定义 hook 封装 Context 消费
const useCommonInfoContext = (): CommonInfoContextData => {
    const context = useContext(CommonInfoContext);
    if (!context) {
        throw new Error('useCommonInfoContext must be used within a CommonInfoProvider');
    }
    return context;
};

// CommonInfoProvider 组件
interface CommonInfoProviderProps {
    children: ReactNode;
}

const CommonInfoProvider: React.FC<CommonInfoProviderProps> = ({ children }) => {
    const [commonInfo, dispatch] = useReducer(commonInfoReducer, initialCommonInfo);

    return <CommonInfoContext.Provider value={{ commonInfo, dispatch }}>{children}</CommonInfoContext.Provider>;
};

export { commonInfoReducer, initialCommonInfo, CommonInfoContext, useCommonInfoContext };

export default CommonInfoProvider;
